import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const SERVICES = ["介護保険", "医療保険", "精神科訪問看護", "予防訪問看護"];
const ADDITIONS = [
  "24時間対応体制加算",
  "緊急時訪問看護加算",
  "特別管理加算",
  "看護体制強化加算",
  "退院時共同指導加算",
  "精神科緊急訪問看護加算",
  "ターミナルケア加算",
  "看護職員処遇改善加算",
];

export default function Organization() {
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get("/organization").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    await api.put("/organization", form);
    toast.success("事業所情報を保存しました");
  };

  const toggleList = (field, value) => {
    const list = form[field] || [];
    const has = list.includes(value);
    setForm({ ...form, [field]: has ? list.filter((x) => x !== value) : [...list, value] });
  };

  const F = (label, key, props = {}) => (
    <div className={props.full ? "md:col-span-2" : ""}>
      <Label>{label}</Label>
      {props.textarea ? (
        <Textarea value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} rows={props.rows || 3} />
      ) : (
        <Input type={props.type || "text"} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="organization-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">ORGANIZATION</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">事業所情報</h1>
        <p className="text-[#4A4A48] mt-1">書類への自動差し込み元</p>
      </div>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">基本情報</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F("事業所名", "name")}
          {F("略称", "short_name")}
          {F("事業所種別", "service_type")}
          {F("法人名", "corporation_name")}
          {F("法人種別", "corporation_type")}
          {F("代表者氏名", "representative")}
          {F("管理者(管理者看護師)氏名", "manager_name")}
          {F("開設年月日", "established_date", { type: "date" })}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">所在・連絡</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F("郵便番号", "postal_code")}
          {F("住所", "address", { full: true })}
          {F("電話番号", "phone")}
          {F("FAX", "fax")}
          {F("メール", "email")}
          {F("ホームページ", "website")}
          {F("24時間対応連絡先", "emergency_phone")}
          {F("営業時間", "business_hours")}
          {F("定休日", "holidays")}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">指定番号</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F("介護保険事業所番号", "care_insurance_number")}
          {F("医療保険指定番号", "medical_insurance_number")}
          {F("精神科訪問看護届出番号", "psych_insurance_number")}
          {F("指定年月日", "designation_date", { type: "date" })}
          {F("指定更新年月日", "designation_renewal_date", { type: "date" })}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">提供サービス</h3>
        <div className="flex flex-wrap gap-2">
          {SERVICES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleList("services", s)}
              className={`px-3 py-1.5 text-sm rounded-full border transition ${
                (form.services || []).includes(s)
                  ? "bg-[#2C5545] text-white border-[#2C5545]"
                  : "bg-white text-[#4A4A48] border-[#E5E5E3]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">加算</h3>
        <div className="flex flex-wrap gap-2">
          {ADDITIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleList("additions", s)}
              className={`px-3 py-1.5 text-sm rounded-full border transition ${
                (form.additions || []).includes(s)
                  ? "bg-[#2C5545] text-white border-[#2C5545]"
                  : "bg-white text-[#4A4A48] border-[#E5E5E3]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3]">
        <h3 className="text-lg font-bold mb-4">運営</h3>
        <div className="grid grid-cols-1 gap-4">
          {F("運営規程", "operation_rules", { textarea: true, rows: 6 })}
          {F("苦情相談窓口", "complaint_contact")}
        </div>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button data-testid="save-org-button" className="bg-[#2C5545] text-white shadow-lg" onClick={save}>保存</Button>
      </div>
    </div>
  );
}
